import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { request } from '../api/client'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'

interface CoverageRule {
  id: string
  title: string
  level: string
  status: string
  running: boolean
  disabled_reason: string
  findings: number
}

interface CoverageCell {
  technique: string
  url: string
  state: 'firing' | 'quiet' | 'blind'
  findings: number
  rules: CoverageRule[]
}

interface CoverageTactic {
  tactic: string
  name: string
  techniques: CoverageCell[]
  firing: number
  quiet: number
  blind: number
}

export interface CoverageData {
  tenant: string
  generated_at: string
  window_days: number
  ruleset: {
    rules: number
    streaming: number
    aggregating: number
    techniques: number
    failed: { title: string; path: string; reason: string }[]
    complete: boolean
  }
  tactics: CoverageTactic[]
  blind_spots: string[]
}

/**
 * ATT&CK coverage — what this tenant detects, and what it does not.
 *
 * Deliberately not a grid of the whole matrix with a handful of lit cells. A grid
 * invites the reader to see colour and stop reading, and the number it produces
 * — "we cover 7% of ATT&CK" — measures the matrix rather than the estate. What is
 * drawn instead is the coverage that exists, sorted into the three states that
 * actually differ for an operator: firing, covered but quiet, and covered by a rule
 * that is switched off. The third one is the reason this screen exists, because it is
 * the one that looks like coverage from every other screen in the product.
 *
 * State is never carried by colour alone. Every cell also says its state in a word,
 * for the same reason severity does (styles/tokens.css).
 */
export function CoveragePage({ tenants = [] }: { tenants?: string[] }) {
  const [tenant] = useTenant(tenants)
  const query = useQuery({
    queryKey: ['coverage', tenant],
    queryFn: () =>
      request<CoverageData>(
        `/coverage/${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`,
      ),
  })

  if (query.isError) return <Failure error={query.error} onRetry={() => query.refetch()} />
  if (!query.data)
    return (
      <div aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => (
          <div className="skeleton" key={index} />
        ))}
      </div>
    )

  const data = query.data
  const covered = data.tactics.reduce((sum, tactic) => sum + tactic.techniques.length, 0)

  return (
    <section aria-labelledby="coverage-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="coverage-heading" style={{ margin: 0 }}>
          Coverage
        </h2>
        <span className="muted">
          {data.ruleset.rules} rule{data.ruleset.rules === 1 ? '' : 's'} · {data.ruleset.techniques}{' '}
          technique{data.ruleset.techniques === 1 ? '' : 's'} · firing counted over the last{' '}
          {data.window_days} days
        </span>
      </header>

      {data.blind_spots.length > 0 ? (
        <div className="error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
          <strong>
            {data.blind_spots.length} thing(s) look like coverage and are not detecting
          </strong>
          <ul style={{ marginBottom: 0 }}>
            {data.blind_spots.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p
          className="panel"
          style={{ borderColor: 'var(--ok)', marginTop: 'var(--space-4)' }}
          role="status"
        >
          Every rule in the ruleset compiled, and none of the coverage below is switched off.
        </p>
      )}

      {/* Said once, plainly, instead of implied by a field of dark cells. */}
      <p className="muted" style={{ marginTop: 'var(--space-4)' }}>
        {covered} technique{covered === 1 ? '' : 's'} across {data.tactics.length} tactic
        {data.tactics.length === 1 ? '' : 's'} are covered by a rule. <strong>Everything not
        shown on this page is not covered</strong> — this is the ruleset, not a scorecard against
        the whole ATT&amp;CK matrix.
      </p>

      <div
        style={{
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
          marginTop: 'var(--space-4)',
          alignItems: 'start',
        }}
      >
        {data.tactics.map((tactic) => (
          <section className="panel" key={tactic.tactic} aria-labelledby={`tactic-${tactic.tactic}`}>
            <h3
              id={`tactic-${tactic.tactic}`}
              style={{ marginTop: 0, marginBottom: 'var(--space-1)', fontSize: 'var(--text-md)' }}
            >
              {tactic.name}
            </h3>
            <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 0 }}>
              {tactic.firing} firing · {tactic.quiet} quiet
              {tactic.blind > 0 ? ` · ${tactic.blind} switched off` : ''}
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {tactic.techniques.map((cell) => (
                <li key={cell.technique} style={{ marginBottom: 'var(--space-2)' }}>
                  <Cell cell={cell} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {data.tactics.length === 0 && (
        <p className="empty">
          No rule in the ruleset declares an ATT&amp;CK technique. That is a mapping gap, not an
          empty estate.
        </p>
      )}
    </section>
  )
}

const STATE: Record<CoverageCell['state'], { word: string; colour: string }> = {
  firing: { word: 'firing', colour: 'var(--sev-medium)' },
  quiet: { word: 'covered, quiet', colour: 'var(--ok)' },
  blind: { word: 'switched off', colour: 'var(--bad)' },
}

function Cell({ cell }: { cell: CoverageCell }) {
  const state = STATE[cell.state]
  return (
    <div
      style={{
        borderLeft: `3px solid ${state.colour}`,
        paddingLeft: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
        {/* The pivot is the point: "we detect this" is a claim an analyst should be
            able to check against what it actually raised. A technique nothing has
            raised is not a link to an empty queue — it says so instead. */}
        {cell.findings > 0 ? (
          <Link className="mono" to={`/queue?technique=${encodeURIComponent(cell.technique)}`}>
            {cell.technique}
          </Link>
        ) : (
          <span className="mono">{cell.technique}</span>
        )}
        <a
          className="muted"
          style={{ fontSize: 'var(--text-xs)' }}
          href={cell.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          ATT&amp;CK<span className="visually-hidden"> page for {cell.technique}</span>
        </a>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: state.colour }}>
        {state.word}
        {cell.findings > 0 ? ` · ${cell.findings} alert${cell.findings === 1 ? '' : 's'}` : ''}
      </p>
      <ul className="muted" style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 'var(--text-xs)' }}>
        {cell.rules.map((rule) => (
          <li key={rule.id}>{describe(rule)}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * One rule, in one sentence.
 *
 * Built as a single string rather than composed of elements so that it is one text
 * node: a title and its reason split across nodes are read as two fragments by a
 * screen reader, and "SSH brute force" followed separately by "disabled: too noisy"
 * is a pair of statements the listener has to join themselves.
 */
function describe(rule: CoverageRule): string {
  if (rule.running) return rule.title
  const reason = rule.disabled_reason ? `: ${rule.disabled_reason}` : ''
  return `${rule.title} — ${rule.status}${reason}`
}
